import { Component, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { Moment } from 'moment'
import { forkJoin } from 'rxjs/internal/observable/forkJoin'
import { finalize } from 'rxjs/internal/operators/finalize'
import { ClientDto } from 'src/app/models/client/client-dto'
import { Datum } from 'src/app/models/common/datum'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { ConfigService } from 'src/app/services/config/config.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { ReddiService } from 'src/app/services/reddi/reddi.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-reddi-report-form',
  templateUrl: './reddi-report-form.component.html',
  styleUrls: ['./reddi-report-form.component.scss'],
  standalone: false,
})
export class ReddiReportFormComponent implements OnInit {
  initialForm: FormGroup = new FormGroup({
    clinicianName: new FormControl('', [Validators.required]),
    title: new FormControl('', [Validators.required]),
    fromDate: new FormControl<Moment>(null, [
      Validators.required,
      this.futureDateValidator(),
    ]),
    toDate: new FormControl<Moment>(null, [
      Validators.required,
      this.futureDateValidator(),
    ]),
    comment: new FormControl(''),
  })
  _todayDate: Date

  dropdowns: {
    label: string
    data: Datum[]
    control: FormControl
    required: boolean
    errorText?: string
  }[] = []
  clientId: string
  _clientDto: ClientDto
  _stillProcessing: boolean = true
  _clientName: string
  _age: string
  _dob: string
  _dateOfBirth: Date = new Date()
  _showClientDetails: boolean = false
  _dateFormat: string = 'Do MMMM, YYYY'
  submitted: boolean = false

  protected logoFolderPath: string =
    this.config.getSettings('spa').imageFolder + '/reddi-logo/'
  protected reddiLogoPath: string =
    this.logoFolderPath + 'Reddi-Logo-168x62.png'
  protected path: string

  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  /**
   * Injecting Router, ActivatedRoute
   * Injecting ReddiSerivce, ClientService, SpinnerService, BanngerService, Config Service
   */
  constructor(
    public reddiService: ReddiService,
    private router: Router,
    private clientService: ClientService,
    private route: ActivatedRoute,
    public spinnerService: SpinnerService,
    public bannerService: BannerService,
    private config: ConfigService,
    private commonService: CommonService,
    public prevRoute: PreviousRouteService,
    private translateService: TranslateService,
  ) {
    this._todayDate = new Date()
  }

  ngOnInit(): void {
    this.path = this.reddiLogoPath
    this.initializeData()

    this.translateService.onLangChange.subscribe(() => {
      this.initializeData()
    })
  }

  initializeData() {
    this.initDropdowns()
    this.mapClientToControls()
    this.getData()
  }

  initDropdowns() {
    this.dropdowns = []
    this.dropdowns.push({
      label: this.commonService.translateSafe('Everyday'),
      data: [
        { label: this.commonService.translateSafe('Yes'), value: '1' },
        { label: this.commonService.translateSafe('No'), value: '0' },
      ],
      control: new FormControl('1'),
      required: true,
    })
    this.dropdowns.push({
      label: this.commonService.translateSafe('Left'),
      data: [
        { label: this.commonService.translateSafe('Yes'), value: '1' },
        { label: this.commonService.translateSafe('No'), value: '0' },
      ],
      control: new FormControl('1'),
      required: true,
    })
    this.dropdowns.push({
      label: this.commonService.translateSafe('Right'),
      data: [
        { label: this.commonService.translateSafe('Yes'), value: '1' },
        { label: this.commonService.translateSafe('No'), value: '0' },
      ],
      control: new FormControl('1'),
      required: true,
    })
    this.dropdowns.push({
      label: this.commonService.translateSafe('Summary sentence'),
      data: [
        {
          label: this.commonService.translateSafe(
            'Able to hear the full range of speech sounds in a quiet environment',
          ),
          value: '1',
        },
        {
          label: this.commonService.translateSafe(
            'Inconsistent access to the range of speech sounds in a quiet environment',
          ),
          value: '2',
        },
        {
          label: this.commonService.translateSafe(
            'Unable to hear the full range of speech sounds in a quiet environment',
          ),
          value: '3',
        },
      ],
      control: new FormControl(''),
      required: true,
    })
  }

  getData() {
    this.spinnerService.show()
    this.clientId = this.route.snapshot.params['clientId']
    forkJoin([this.clientService.Details(this.clientId)])
      .pipe(
        finalize(() => {
          this.spinnerService.hide()
          this._stillProcessing = false
        }),
      )
      .subscribe(
        ([clientDto]) => {
          this._clientDto = clientDto
          this._clientName = this._clientDto.preferredFullName
          this._age = this._clientDto.age
          this._dateOfBirth = this._clientDto.dateOfBirth
          this._dob = moment(this._dateOfBirth).format(this._dateFormat)
          this._showClientDetails = true
          this.checkToolsAccess()
          this.retriveIndicators(clientDto)
        },
        (error) => {
          this.spinnerService.hide()
          throw error
        },
      )
  }

  GetDateFromErrorText(): string {
    if (this.initialForm.controls['fromDate'].getError('inFuture') != null) {
      return 'Date cannot be in the future.'
    } else if (
      this.initialForm.controls['fromDate'] >
      this.initialForm.controls['toDate']
    ) {
      return 'From Date can not be greater than To Date'
    }
    return 'Date is invalid.'
  }

  GetDateToErrorText(): string {
    if (this.initialForm.controls['toDate'].getError('inFuture') != null) {
      return 'Date cannot be in the future.'
    }
    return 'Date is invalid.'
  }

  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value

      if (!value) {
        return null
      }

      // isAfter() will default to current date time and the field doesn't use time
      // so value = 1/1/2020 00:00:00 now = 1/1/2020 01:38:21
      return moment(value).isAfter() ? { inFuture: true } : null
    }
  }

  mapClientToControls() {
    const controls = this.initialForm.controls

    controls['fromDate'].setValue(moment(Date()))
    controls['toDate'].setValue(moment(Date()))
    // Since all dropdowns have the same values, we can now set up the form controls for each
    this.dropdowns.forEach((dropdown, index) => {
      const controlName = 'dropdown' + (index + 1)
      this.initialForm.addControl(controlName, dropdown.control)
    })
  }

  hasError(controlName: string, errorName: string) {
    const control = this.initialForm.controls[controlName]
    return control?.hasError(errorName) && (control.touched || this.submitted)
  }

  onSubmit() {
    this.submitted = true
    if (this.initialForm.valid) {
      this.router.navigate(['reddi/external-report', this.clientId], {
        queryParams: this.initialForm.value,
      })
    }
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDto,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDto,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDto,
    )
  }

  retriveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.prevRoute.getPreviousUrl()
    if (prevUrl) {
      this.prevRoute.goToPreviousUrl(prevUrl)
    }
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this.clientId)
  }
}
